import { expressionsService } from '@services/domain-factories/expressions';
import { getReportResourceId } from '@services/domain-factories/reports';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { Expression, Form, FormModel, WebReport } from '@vincerosoft/assessfluence-types';

type DesignerState = {
  linkedTffFormModel: FormModel | null;
  linkedExpressions: Expression[] | null;
  itemId: string | null;
  itemType: 'form' | 'report';
  intent: Form['intent'] | 'report' | null;
};

type State = {
  byItemId: Record<string, DesignerState>;
  activeItemId: string | null;
};

const createInitialItemState = (): DesignerState => ({
  linkedTffFormModel: null,
  linkedExpressions: null,
  itemId: null,
  itemType: 'form',
  intent: null
});

type Actions = {
  getOrInit: (
    itemId: string,
    type: 'form' | 'report',
    intent: Form['intent'] | 'report' | null
  ) => DesignerState;
  getExpressions: (
    itemId: string,
    intent: Form['intent'] | 'report' | null,
    item: Form | WebReport
  ) => Promise<Expression[]>;
  reset: (itemId: string) => void;
  resetAll: () => void;
  setFormModel: (itemId: string, formModel: FormModel | null) => void;
};

export const globalDesignerStore = createStoreWithSelector<State & Actions>()((set, get) => ({
  byItemId: {},
  activeItemId: null,

  getOrInit: (itemId, type, intent) => {
    const current = get().byItemId[itemId];
    set({ activeItemId: itemId });
    if (current) return current;

    const next = createInitialItemState();
    next.itemType = type;
    next.intent = intent;
    set((state) => ({
      byItemId: {
        ...state.byItemId,
        [itemId]: next
      }
    }));
    return next;
  },

  getExpressions: async (itemId, intent, item) => {
    const state = get().byItemId[itemId];
    if (state?.linkedExpressions) {
      return state.linkedExpressions;
    }

    const getExpressions = () => {
      if (intent === 'report') {
        const report = item as WebReport;
        const sourceId = getReportResourceId(report);
        if (!sourceId) throw new Error('No source ID');

        return expressionsService.getExpressionsByScopeAndSource('web_report', sourceId);
      }
      return expressionsService.getExpressionsByScopeAndSource('runtime', (item as Form).id!);
    };

    const expressions = await getExpressions();

    set((state) => ({
      byItemId: {
        ...state.byItemId,
        [itemId]: {
          ...(state.byItemId[itemId] ?? createInitialItemState()),
          linkedExpressions: expressions
        }
      }
    }));
    return expressions;
  },

  setFormModel: (itemId, formModel) => {
    set((state) => ({
      byItemId: {
        ...state.byItemId,
        [itemId]: {
          ...(state.byItemId[itemId] ?? createInitialItemState()),
          linkedTffFormModel: formModel
        }
      }
    }));
  },

  reset: (itemId) =>
    set((state) => ({
      byItemId: {
        ...state.byItemId,
        [itemId]: createInitialItemState()
      }
    })),

  resetAll: () => set({ byItemId: {} })
}));

export const useGlobalDesignerStore = createUseStore(globalDesignerStore);
